import { useEffect, useState } from "react";
import {
  InputGroup,
  LargeBtn,
  InputSelect,
  NumberInputGroup,
  Button,
  color,
} from "@funded-here-interface/common";
import { Modal, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import useGetVirtualAccounts from "../../../hooks/useGetVirtualAccounts";
import { toast } from "react-toastify";
import useInternalTransfer from "../../../hooks/useInternalTransfer";
import { useQueryClient } from "react-query";
import {
  Filter,
  VaAccountTab,
  VirtualAccount,
} from "../../../services/getVirtualAccounts";
import {
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_DEV,
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_PROD,
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_UAT,
  vaAccountTypesOption,
} from "@funded-here-interface/common/src/constant/constant";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";
// Constants
const vaTypes = vaAccountTypesOption;

const InternalTransfer = () => {
  // Redux selectors
  const { token, env } = useSelector((state: RootState) => state.auth);

  // React Query
  const queryClient = useQueryClient();

  // State - Form fields
  const [senderID, setSenderId] = useState("");
  const [receiverID, setReceiverId] = useState("");
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("0.000000");
  const [remarks, setRemarks] = useState("");

  // State - VA types
  const [sender_va_type, setSenderVAType] = useState("");
  const [receiver_va_type, setReceiverVAType] = useState("");

  // State - Balances
  const [senderBalance, setSenderBalance] = useState("");
  const [receiverBalance, setReceiverBalance] = useState("");

  // State - Organization details
  const [senderOrgName, setSenderOrgName] = useState("");
  const [receiverOrgName, setReceiverOrgName] = useState("");
  const [senderOrgDetails, setSenderOrgDetails] = useState("");
  const [receiverOrgDetails, setReceiverOrgDetails] = useState("");

  // State - Bank accounts
  const [senderVirtualAccountId, setSenderVirtualAccountId] = useState("");
  const [receiverVirtualAccountId, setReceiverVirtualAccountId] = useState("");

  // State - Filters and lists
  const [senderFilter, setSenderFilter] = useState<Filter[]>([]);
  const [receiverFilter, setReceiverFilter] = useState<Filter[]>([]);
  const [senderVaAccountList, setSenderVaAccountList] = useState<
    VirtualAccount[]
  >([]);
  const [receiverVaAccountList, setReceiverVaAccountList] = useState<
    VirtualAccount[]
  >([]);

  // State - Junior investor account ID
  const [juniorInvestorAccountId, setJuniorInvestorAccountId] = useState("");

  // State - Form errors
  const [formError, setFormError] = useState({
    senderID: "",
    receiverID: "",
    currency: "",
    amount: "",
    remarks: "",
    sender_va_type: "",
    receiver_va_type: "",
  });

  // Hooks
  const [opened, { open, close }] = useDisclosure(false);
  const useInternalTransferMutation = useInternalTransfer();

  const { refetch: refetchSender } = useGetVirtualAccounts(
    {
      token,
      filter: senderFilter.length > 0 ? senderFilter : undefined,
      tab: VaAccountTab.INTERNAL_TRANSFER,
    },
    false
  );

  const { refetch: refetchReceiver } = useGetVirtualAccounts(
    {
      token,
      filter: receiverFilter.length > 0 ? receiverFilter : undefined,
      tab: VaAccountTab.INTERNAL_TRANSFER,
    },
    false
  );

  // Helper functions
  const getFilter = (vaType: string, currency: string) => {
    const filterInformation = [
      { id: "type", value: vaType },
      { id: "currency", value: currency },
    ];
    if (vaType === "investor") {
      filterInformation.push({
        id: "accountId",
        value: juniorInvestorAccountId,
      });
    }

    return filterInformation;
  };

  const fetchSender = async () => {
    const result = await refetchSender();
    setSenderVaAccountList(result.data?.vaAccounts ?? []);
  };

  const fetchReceiver = async () => {
    const result = await refetchReceiver();
    setReceiverVaAccountList(result.data?.vaAccounts ?? []);
  };

  const clearSender = () => {
    setSenderId("");
    setSenderBalance("");
    setSenderVirtualAccountId("");
    setSenderOrgName("");
    setSenderOrgDetails("");
  };

  const clearReceiver = () => {
    setReceiverId("");
    setReceiverBalance("");
    setReceiverVirtualAccountId("");
    setReceiverOrgName("");
    setReceiverOrgDetails("");
  };

  const clear = () => {
    clearSender();
    clearReceiver();
    setRemarks("");
    setAmount("0.00");
    setSenderVAType("");
    setReceiverVAType("");
    setCurrency("");
    setReceiverVaAccountList([]);
    setSenderVaAccountList([]);
    setSenderFilter([]);
    setReceiverFilter([]);
  };

  // Event handlers
  const handleBlurAmount = () => {
    let parseAmount = parseFloat(amount).toFixed(6);
    setAmount(parseAmount);
  };

  const handleSenderChange = (orgDetails: string) => {
    formError.senderID = "";
    const selectedOrg = senderVaAccountList.find(
      (item) => item.orgDetails === orgDetails
    );

    if (selectedOrg) {
      setSenderId(selectedOrg.id);
      setSenderBalance(selectedOrg.balance);
      setSenderVirtualAccountId(selectedOrg.accountId);
      setSenderOrgName(
        selectedOrg.orgName ? selectedOrg.orgName : selectedOrg.refId
      );
      setSenderOrgDetails(orgDetails);
    }
  };

  const handleReceiverChange = (orgDetails: string) => {
    formError.receiverID = "";
    const selectedOrg = receiverVaAccountList.find(
      (item) => item.orgDetails === orgDetails
    );
    if (selectedOrg) {
      setReceiverId(selectedOrg.id);
      setReceiverBalance(selectedOrg.balance);
      setReceiverVirtualAccountId(selectedOrg.accountId);
      setReceiverOrgName(
        selectedOrg.orgName ? selectedOrg.orgName : selectedOrg.refId
      );
      setReceiverOrgDetails(orgDetails);
    }
  };

  const handleSenderVaTypeChange = async (vaType: string) => {
    setSenderVaAccountList([]);
    clearSender();
    setSenderVAType(vaType);
    if (!currency) {
      return;
    }

    const filterInformation = getFilter(vaType, currency);

    setSenderFilter(filterInformation);
  };

  const handleReceiverVaTypeChange = async (vaType: string) => {
    setReceiverVaAccountList([]);
    clearReceiver();
    await setReceiverVAType(vaType);
    if (!currency) {
      return;
    }
    const filterInformation = getFilter(vaType, currency);

    await setReceiverFilter(filterInformation);
    const result = await refetchReceiver();
    await setReceiverVaAccountList(
      result.data?.vaAccounts ? result.data.vaAccounts : []
    );
  };

  const handleSubmit = () => {
    const newErrors = {
      senderID: "",
      receiverID: "",
      currency: "",
      amount: "",
      remarks: "",
      sender_va_type: "",
      receiver_va_type: "",
    };

    // Validation rules
    const validations = [
      {
        condition: !senderID,
        field: "senderID",
        message: "Sender is required",
      },
      {
        condition: !receiverID,
        field: "receiverID",
        message: "Receiver is required",
      },
      {
        condition: !currency,
        field: "currency",
        message: "Currency is required",
      },
      { condition: !amount, field: "amount", message: "Amount is required" },
      { condition: !remarks, field: "remarks", message: "Remarks is required" },
      {
        condition: !sender_va_type,
        field: "sender_va_type",
        message: "Sender Virtual Account Type is required",
      },
      {
        condition: !receiver_va_type,
        field: "receiver_va_type",
        message: "Receiver Virtual Account Type is required",
      },
      {
        condition: parseFloat(amount) > parseFloat(senderBalance),
        field: "amount",
        message: `Amount transferred is more than the balance: ${currency} ${senderBalance}`,
      },
    ];

    // Apply validations
    validations.forEach(({ condition, field, message }) => {
      if (condition) {
        newErrors[field as keyof typeof newErrors] = message;
      }
    });

    setFormError(newErrors);

    // Check if form is valid (no errors)
    const isValid = Object.values(newErrors).every((error) => error === "");

    if (isValid) {
      open();
    }
  };

  const confirmTransfer = () => {
    useInternalTransferMutation.mutate(
      {
        token: token,
        senderVirtualAccountPrimaryId: parseInt(senderID, 10),
        receiverVirtualAccountPrimaryId: parseInt(receiverID, 10),
        currency: currency,
        amount: amount.toString(),
        remarks: remarks,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries("get-vaacounts");
          toast.success("Transfer Executed");
          close();
          clear();
        },
        onError: (error) => {
          toast.error((error as Error).message);
        },
      }
    );
  };

  // Effects
  useEffect(() => {
    const junior_dev: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_DEV;
    const junior_uat: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_UAT;
    const junior_prod: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_PROD;

    let junior: string = "";

    if (env === "local") {
      junior = junior_dev;
    } else if (env === "uat") {
      junior = junior_uat;
    } else if (env === "production") {
      junior = junior_prod;
    }

    setJuniorInvestorAccountId(junior);
  }, []);

  useEffect(() => {
    if (senderFilter.length > 0) {
      fetchSender();
    }
  }, [senderFilter]);

  useEffect(() => {
    if (receiverFilter.length > 0) {
      fetchReceiver();
    }
  }, [receiverFilter]);

  return (
    <div>
      <div style={{ width: "44%" }}>
        <label>Currency</label>
        <InputSelect
          isErrored={formError.currency}
          onChange={(value: string) => {
            formError.currency = "";
            setCurrency(value);
          }}
          labelText=""
          id="currency"
          value={currency}
          data={["SGD"]}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <div style={{ width: "50%", marginRight: 60 }}>
          <label>Sender Virtual Account Type</label>
          <InputSelect
            isErrored={formError.sender_va_type}
            onChange={(value: string) => {
              formError.sender_va_type = "";
              const selectedType = vaTypes.find(
                (option) => option.label === value
              );
              if (selectedType) {
                handleSenderVaTypeChange(selectedType.value);
              }
            }}
            labelText=""
            id="senderVA"
            value={
              vaTypes.find((option) => option.value === sender_va_type)
                ?.label || null
            }
            data={vaTypes.map((option) => option.label)}
            disabled={!currency}
          />
        </div>

        <div style={{ width: "50%", marginRight: 60 }}>
          <label>Receiver Virtual Account Type</label>
          <InputSelect
            isErrored={formError.receiver_va_type}
            onChange={(value: string) => {
              formError.receiver_va_type = "";
              const selectedType = vaTypes.find(
                (option) => option.label === value
              );
              if (selectedType) {
                handleReceiverVaTypeChange(selectedType.value);
              }
            }}
            labelText=""
            id="receiverVA"
            value={
              vaTypes.find((option) => option.value === receiver_va_type)
                ?.label || null
            }
            data={vaTypes.map((option) => option.label)}
            disabled={!currency}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <div style={{ width: "50%", marginRight: 60 }}>
          <label>Sender Virtual Account ID</label>
          <InputSelect
            isErrored={formError.senderID}
            onChange={(value: string) => {
              formError.senderID = "";
              handleSenderChange(value);
            }}
            labelText=""
            id="sender"
            value={senderOrgDetails}
            data={senderVaAccountList.map((item) => item.orgDetails as string)}
            disabled={senderVaAccountList.length <= 0}
          />
        </div>
        <div style={{ width: "50%", marginRight: 60 }}>
          <label>Receiver Virtual Account ID</label>
          <InputSelect
            isErrored={formError.receiverID}
            onChange={(value: string) => {
              formError.receiverID = "";
              handleReceiverChange(value);
            }}
            labelText=""
            id="receiver"
            value={receiverOrgDetails}
            data={receiverVaAccountList.map(
              (item) => item.orgDetails as string
            )}
            disabled={receiverVaAccountList.length <= 0}
          />
        </div>
      </div>

      <div style={{ width: "50%", marginRight: 60, marginTop: 20 }}>
        <label>Amount</label>
        <NumberInputGroup
          isErrored={formError.amount}
          onChange={(value: string) => {
            formError.amount = "";
            setAmount(value);
          }}
          labelText=""
          id="amount"
          value={amount}
          min={0}
          onBlur={handleBlurAmount}
          decimalPlaces={6}
        />
      </div>
      <div style={{ width: "100%", marginRight: 80 }}>
        <label>Remarks</label>

        <InputGroup
          isErrored={formError.remarks}
          onChange={(value: string) => {
            formError.remarks = "";
            setRemarks(value);
          }}
          labelText=""
          value={remarks}
          id="remarks"
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "start",
          marginTop: "40px",
        }}
      >
        <LargeBtn
          onClick={handleSubmit}
          backgroundColor="#3786D6"
          textColor="#FFFFFF"
          width="auto"
        >
          Transfer
        </LargeBtn>
      </div>

      <Modal
        opened={opened}
        onClose={close}
        size="lg"
        title={
          <Title order={4} style={{ fontWeight: "bold" }}>
            Confirm Internal Transfer
          </Title>
        }
      >
        <div style={{ padding: "0px 30px" }}>
          <p style={{ padding: "10px 0px" }}>
            Do you want to execute the following internal transfer?
          </p>

          <p>
            <strong>Sender Virtual Account ID:</strong> {senderVirtualAccountId}
          </p>
          <p>
            <strong>Sender:</strong> {senderOrgName}
          </p>

          <p>
            <strong>Receiver Virtual Account ID:</strong>{" "}
            {receiverVirtualAccountId}
          </p>
          <p>
            <strong>Receiver:</strong> {receiverOrgName}
          </p>

          <p>
            <strong>Amount:</strong> {currency}{" "}
            {formatNumbersWithThousandSeperators(parseFloat(amount), false, 6)}
          </p>
          {senderVirtualAccountId !== receiverVirtualAccountId && (
            <>
              <p>
                <strong>Sender Original Balance:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance),
                  false,
                  6
                )}
              </p>
              <p>
                <strong>Sender Balance After Transfer:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance) - parseFloat(amount),
                  false,
                  6
                )}
              </p>

              <p>
                <strong>Receiver Original Balance:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(receiverBalance),
                  false,
                  6
                )}
              </p>
              <p>
                <strong>Receiver Balance After Transfer:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(receiverBalance) + parseFloat(amount),
                  false,
                  6
                )}
              </p>
            </>
          )}

          {/* Display this only for internal transfer within the same Virtual Account */}
          {senderVirtualAccountId === receiverVirtualAccountId && (
            <>
              <p>
                <strong>Sender Original Balance:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance),
                  false,
                  6
                )}
              </p>
              <p>
                <strong>Sender Balance After Transfer:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance) - parseFloat(amount),
                  false,
                  6
                )}
              </p>

              <p>
                <strong>Receiver Original Balance:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance) - parseFloat(amount),
                  false,
                  6
                )}
              </p>
              <p>
                <strong>Receiver Balance After Transfer:</strong> {currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(senderBalance),
                  false,
                  6
                )}
              </p>
            </>
          )}

          <p>
            <strong>Remarks:</strong> {remarks}
          </p>

          <div style={{ display: "flex" }}>
            <div style={{ margin: "10px auto" }}>
              <div style={{ paddingBottom: "10px" }}>
                <Button
                  onClick={confirmTransfer}
                  backgroundColor={color.FHGREEN}
                  textColor={color.WHITE}
                  children="Confirm"
                ></Button>
              </div>
              <div>
                <Button
                  onClick={close}
                  backgroundColor={color.REJECT}
                  textColor={color.WHITE}
                  children="Cancel"
                ></Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InternalTransfer;
